import { getGetMainWebShopConfigServerMockHandler } from '@as-react/common-queries/msw/configuration-service';
import { getGetPopularBrandsServerMockHandler } from '@as-react/common-queries/msw/product-service';
import { http, HttpResponse } from 'msw';

import { type WebshopMockConfig, webshopConfigs } from './webshopConfigs';

const DEFAULT_BRAND = 'asadventure';

function getConfigForBrand(brand: string): WebshopMockConfig {
  return webshopConfigs[brand] ?? webshopConfigs[DEFAULT_BRAND]!;
}

/**
 * Extracts the mainWebShop name from an intercepted URL where possible.
 * Falls back to the provided default brand.
 */
function extractBrandFromUrl(url: string, fallback: string): string {
  // mainwebshop/name/{brand} endpoint
  const mainWebShopMatch = url.match(/\/mainwebshop\/name[:/]([^/?]+)/);
  if (mainWebShopMatch?.[1]) return mainWebShopMatch[1];

  // AEM config URL: /content/www-{brand}-com/ or /content/platform-blueprints/{brand}/
  const aemConfigMatch = url.match(/\/content\/(?:www-([^/]+?)-(?:com|nl|be)|platform-blueprints\/([^/]+))\//);
  if (aemConfigMatch) {
    const brand = aemConfigMatch[1] ?? aemConfigMatch[2];
    if (brand) return brand;
  }

  // AEM brand resource: /api/services/public/brand/{brand}
  const brandResourceMatch = url.match(/\/api\/services\/public\/brand\/([^/?]+)/);
  if (brandResourceMatch?.[1]) return brandResourceMatch[1];

  return fallback;
}

/**
 * Creates an AEM page config handler that returns brand-appropriate data.
 * In browser mode, it parses the brand from the configUrl.
 * When created with a specific brand, it always returns that brand's config.
 */
export function createAemPageConfigHandler(brand?: string) {
  return http.get('**/content/**/*.config.json', ({ request }) => {
    const resolvedBrand = brand ?? extractBrandFromUrl(request.url, DEFAULT_BRAND);
    const config = getConfigForBrand(resolvedBrand);
    return HttpResponse.json(config.aemPageConfig);
  });
}

/**
 * Creates an AEM brands resource handler.
 * Returns brand entries with the correct mainWebShop value.
 */
export function createAemBrandsResourceHandler(brand?: string) {
  return http.get('*/api/services/public/brand/*', ({ request }) => {
    const resolvedBrand = brand ?? extractBrandFromUrl(request.url, DEFAULT_BRAND);
    return HttpResponse.json([
      {
        id: 'brand-1',
        description: 'Arc teryx',
        fredhopperCode: 'arcteryx',
        active: true,
        visible: true,
        mainWebShop: resolvedBrand,
        relativePath: '/brands/arcteryx',
        alias: 'arcteryx',
        popular: 1,
        logo: 'arcteryx.png',
        logoTimestamp: 1234567890,
      },
      {
        id: 'brand-2',
        description: 'Patagonia',
        fredhopperCode: 'patagonia',
        active: true,
        visible: true,
        mainWebShop: resolvedBrand,
        relativePath: '/brands/patagonia',
        alias: 'patagonia',
        popular: 2,
        logo: 'patagonia.png',
        logoTimestamp: 1234567890,
      },
      {
        id: 'brand-3',
        description: 'The North Face',
        fredhopperCode: 'northface',
        active: true,
        visible: true,
        mainWebShop: resolvedBrand,
        relativePath: '/brands/northface',
        alias: 'northface',
        popular: 3,
        logo: 'northface.png',
        logoTimestamp: 1234567890,
      },
    ]);
  });
}

/**
 * Creates a mainWebShopConfig handler using the Orval-generated mock.
 * In browser mode, it reads the brand from the URL path.
 * When created with a specific brand, it always returns that brand's config.
 */
export function createMainWebShopConfigHandler(brand?: string) {
  return getGetMainWebShopConfigServerMockHandler(info => {
    const resolvedBrand = brand ?? extractBrandFromUrl(info.request.url, DEFAULT_BRAND);
    const config = getConfigForBrand(resolvedBrand);
    return config.mainWebShopConfig;
  });
}

/**
 * Creates a popular brands handler.
 */
export function createPopularBrandsHandler() {
  return getGetPopularBrandsServerMockHandler({
    popularBrands: {
      'brand-1': 100,
      'brand-3': 50,
    },
  });
}

/**
 * Creates a complete set of mock handlers for a given brand.
 * If no brand is specified, handlers will auto-detect the brand from the request URL (browser mode).
 */
export function createMockHandlers(brand?: string) {
  return [
    createAemPageConfigHandler(brand),
    createAemBrandsResourceHandler(brand),
    createMainWebShopConfigHandler(brand),
    createPopularBrandsHandler(),
  ];
}
